import {BooleanConditionallyUndefinedType} from "../../scripts/UtilityTypes";

/**
 * This class manages a type event listener setup so you can quickly add addEventListener() and removeEventListener() to your classes.
 *
 * @example <caption>Example setup.</caption>
 * // Import
 * import EventManager from "../components/EventManager";
 *
 * // Define interfaces for the events which is passed to your listeners:
 * export interface BeginExampleEvent {
 * 	sourceElement: HTMLElement;
 * }
 *
 * // Define a map that indicates the Event object for each event type (for example, a listener for "being" will be passed an instance of BeginExampleEvent):
 * export interface ExampleEventMap {
 * 	"begin": BeginExampleEvent;
 * 	"end":	EndExampleEvent;
 * }
 *
 * // Inside a class create your instances of EventManager and assign your classes' properties to delegate to the related methods on your EventManager instance.
 * // Note: If you don't type your methods (e.g. addEventListener) or make them readonly (the preferred option) then autocomplete will not be typed -- So do it how shown here!
 * private readonly eventManager = new EventManager<ExampleEventMap>();
 * public readonly addEventListener = this.eventManager.addEventListener;
 * public readonly removeEventListener = this.eventManager.removeEventListener;
 *
 * // You can then notify the registered listeners for an
 * someMethod() {
 * 	const newEvent = {sourceElement: this.someElement};
 * 	this.eventManager.notifyEventListeners("begin", newEvent);
 * }
 *
 * In your components destroy remember to run destroy():
 * 	this.eventManager.destroy();
 *
 * @example <caption>If you have some work to perform when an listener is added you can use setOnAdd(). This can also be used to do some init work on the first event listener is registered -- which is shown here.</caption>
 * // In your constructor:
 * this.eventManager.setOnAdd(
 *	() => {
 *		// Init listening for events when the first addOnEventListener is called
 *		this.initNativeAjaxEventListening();
 *		// Clear this "onAdd" so it is only called the first time addOnEventListener is called
 *		this.eventManager.clearOnAdd();
 *	}
 *);
 *
 * TODO @Michael: Document the options of "addEventListener" and how the "onEach" of notifyEventListeners works.
 */
export default class EventManager<EventMap, AddOptionsMap extends {[K in keyof EventMap]?: any} | undefined = undefined> {
	private eventListenersByType: EventListenersByType<EventMap, AddOptionsMap> | undefined;
	private onAdd?: (eventType: keyof EventMap, options?: any) => void;

	constructor() {}

	destroy() {
		this.onAdd = undefined;
		this.eventListenersByType = undefined;
	}

	setOnAdd(onAdd: (eventType: keyof EventMap, options?: any) => void) {
		this.onAdd = onAdd;
	}
	removeOnAdd() {
		this.onAdd = undefined;
	}

	/**
	 * If the eventManager already contains the listener the entry will be updated to have the passed `options`.
	 * @param eventType
	 * @param listener
	 * @param options
	 */
	addEventListener: addEventListenerType<EventMap, AddOptionsMap> = (eventType, listener, options?) => {
		if (this.onAdd !== undefined) {
			this.onAdd(eventType, options);
		}
		this.getEventListeners(eventType, true).set(listener, options);
	}

	removeEventListener: removeEventListenerType<EventMap> = (eventType, listener) => {
		const listeners = this.getEventListeners(eventType);
		return listeners !== undefined && listeners.delete(listener);
	}

	notifyEventListeners: notifyEventListenersType<EventMap, AddOptionsMap> = (eventType, ev, onEach) => {
		const listeners = this.getEventListeners(eventType);
		if (listeners !== undefined) {
			listeners.forEach((listenerOptions, listener) => {
				let shouldCall = true;
				if (onEach !== undefined) {
					shouldCall = onEach.call(null, ev, listenerOptions);
				}
				if (shouldCall) {
					listener.call(null, ev);
				}
			});
		}
	}

	hasEventListeners: hasEventListenersType<EventMap> = (eventType) => {
		if (this.eventListenersByType === undefined) {
			return false;
		}
		const listenersForEventType = this.eventListenersByType[eventType];
		return listenersForEventType !== undefined && listenersForEventType.size > 0;
	}

	/**
	 * Returns a Set of listeners for the event type
	 */
	private getEventListeners<K extends keyof EventMap, C extends boolean>(eventType: K, create?: C):
		BooleanConditionallyUndefinedType<C, Map<(ev: EventMap[K]) => any, NonNullable<AddOptionsMap>[K] | undefined>>;
	private getEventListeners<K extends keyof EventMap>(eventType: K, create: boolean = false):
		Map<(ev: EventMap[K]) => any, NonNullable<AddOptionsMap>[K] | undefined> | undefined
	{
		if (this.eventListenersByType === undefined) {
			if (!create) {
				return undefined;
			}
			this.eventListenersByType = {};
		}
		let eventList = this.eventListenersByType[eventType];
		if (eventList === undefined) {
			if (!create) {
				return undefined;
			}
			eventList = new Map();
			this.eventListenersByType[eventType] = eventList;
		}
		return eventList;
	}
}

type EventListenersByType<EventMap, AddOptionsMap extends {[P in keyof EventMap]?: any} | undefined> = {
	[K in keyof EventMap]?: Map<
		(ev: EventMap[K]) => any,
		NonNullable<AddOptionsMap>[K] | undefined
	>
};

export type addEventListenerType<
	EventMap,
	AddOptionsMap extends {[P in keyof EventMap]?: any} | undefined
> = <
	K extends keyof EventMap,
	Opts extends AddOptionsMap extends undefined ? undefined : NonNullable<AddOptionsMap>[K]
>(
	eventType: K,
	listener: (ev: EventMap[K]) => any,
	options?: Opts
) => void;

export type removeEventListenerType<EventMap> = <K extends keyof EventMap>(eventType: K, listener: (ev: EventMap[K]) => any) => void;

export type notifyEventListenersType<
	EventMap,
	AddOptionsMap extends {[P in keyof EventMap]?: any} | undefined
> = <
	K extends keyof EventMap,
	EV extends EventMap[K],
	Opts extends AddOptionsMap extends undefined ? undefined : NonNullable<AddOptionsMap>[K]
>(eventType: K, ev: EV, onEach?: (ev: EV, listenerOptions?: Opts) => boolean) => void;

export type hasEventListenersType<EventMap> = <K extends keyof EventMap>(eventType: K) => boolean;
